// Sending a library version from the browser, as TUNE Live sends one, and as a person wants to: with its progress,
// paused and resumed (only the parts the cloud doesn't have go again: GET /uploads/{id}), and cancelled (DELETE
// /uploads/{id}, so nothing is left half done). A part that fails is tried twice more before the upload stops.
import type { components } from '@tune/shared';
import { ApiError, data, type Api } from '../api';
import { sha256, type Described } from '../upload';

type UploadDone = components['schemas']['UploadDone'];

export type UploadState =
  | { kind: 'preparing' }
  | { kind: 'sending'; sent: number; total: number }
  | { kind: 'paused'; sent: number; total: number }
  | { kind: 'finishing' }
  | { kind: 'done'; item: { id: string; n: number }; existing: boolean }
  | { kind: 'failed'; error: unknown; sent: number; total: number }
  | { kind: 'cancelled' };

/** What's sent: the next version of an item, or a new one. */
export type Target = { itemId: string } | { newItem: Described };

/** How many part addresses are asked for at once, as the API allows. */
const BATCH = 100;
/** Tries for each part. */
const TRIES = 3;

class Stopped extends Error {}

export class Uploader {
  private uploadId: string | null = null;
  private partSize = 0;
  private partCount = 0;
  private sent = 0;
  private controller: AbortController | null = null;
  private stopping: 'pause' | 'cancel' | null = null;
  state: UploadState = { kind: 'preparing' };

  constructor(
    private readonly api: Api,
    private readonly file: File,
    private readonly target: Target,
    private readonly changed: (state: UploadState) => void,
    private readonly put: typeof fetch = (input, init) => fetch(input, init),
    /** Waits between tries; tests make it instant. */
    private readonly wait: (ms: number) => Promise<void> = (ms) => new Promise((done) => setTimeout(done, ms)),
  ) {}

  private set(state: UploadState): void {
    this.state = state;
    this.changed(state);
  }

  /** Starts sending, from the beginning. */
  async start(): Promise<void> {
    this.stopping = null;
    this.set({ kind: 'preparing' });
    try {
      const start =
        'itemId' in this.target
          ? { purpose: 'libraryVersion' as const, itemId: this.target.itemId }
          : {
              purpose: 'libraryVersion' as const,
              newItem: {
                kind: this.target.newItem.kind,
                identity: this.target.newItem.identity,
                name: this.target.newItem.name,
              },
            };
      const begun = await data(
        this.api.POST('/uploads', {
          body: { ...start, size: this.file.size, sha256: await sha256(this.file), fileName: this.file.name },
        }),
      );
      if ('existing' in begun) {
        this.set({ kind: 'done', item: { id: begun.existing.itemId, n: begun.existing.n }, existing: true });
        return;
      }
      this.uploadId = begun.uploadId;
      this.partSize = begun.partSize;
      this.partCount = begun.partCount;
      this.sent = 0;
      if (this.stopping === 'cancel') {
        // Cancelled while it began: the cloud forgets it too.
        await this.cancel();
        return;
      }
      await this.send(Array.from({ length: this.partCount }, (_, i) => i + 1));
    } catch (error) {
      this.stopped(error);
    }
  }

  /** Goes on from where it stopped: the parts the cloud has stay, the rest go. A lost upload starts again. */
  async resume(): Promise<void> {
    if (!this.uploadId) {
      return this.start();
    }
    this.stopping = null;
    this.set({ kind: 'sending', sent: this.sent, total: this.file.size });
    try {
      const uploadId = this.uploadId;
      let have: Array<{ n: number; size: number }>;
      try {
        have = (await data(this.api.GET('/uploads/{uploadId}', { params: { path: { uploadId } } }))).parts;
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
          this.uploadId = null;
          return this.start();
        }
        throw error;
      }
      const there = new Set(have.map((p) => p.n));
      this.sent = have.reduce((sum, p) => sum + p.size, 0);
      const missing = Array.from({ length: this.partCount }, (_, i) => i + 1).filter((n) => !there.has(n));
      await this.send(missing);
    } catch (error) {
      this.stopped(error);
    }
  }

  /** Stops after the part on its way, which goes again on resuming. */
  pause(): void {
    if (this.state.kind !== 'sending') {
      return;
    }
    this.stopping = 'pause';
    this.controller?.abort();
  }

  /** Gives the upload up: the cloud forgets what it had of it. */
  async cancel(): Promise<void> {
    this.stopping = 'cancel';
    this.controller?.abort();
    const uploadId = this.uploadId;
    this.uploadId = null;
    this.set({ kind: 'cancelled' });
    if (uploadId) {
      // Answered 204 whether or not it's still there; a failure leaves it to run out.
      await this.api.DELETE('/uploads/{uploadId}', { params: { path: { uploadId } } }).catch(() => undefined);
    }
  }

  private async send(numbers: number[]): Promise<void> {
    const uploadId = this.uploadId;
    if (!uploadId) {
      return;
    }
    this.set({ kind: 'sending', sent: this.sent, total: this.file.size });
    for (let i = 0; i < numbers.length; i += BATCH) {
      const { parts } = await data(
        this.api.POST('/uploads/{uploadId}/parts', {
          params: { path: { uploadId } },
          body: { numbers: numbers.slice(i, i + BATCH) },
        }),
      );
      for (const part of parts) {
        await this.sendPart(part);
        this.sent += part.size;
        this.set({ kind: 'sending', sent: this.sent, total: this.file.size });
      }
    }
    this.set({ kind: 'finishing' });
    const done: UploadDone = await data(
      this.api.POST('/uploads/{uploadId}/complete', { params: { path: { uploadId } }, body: {} }),
    );
    if (!('item' in done)) {
      throw new ApiError(500, 'unknown', 'the upload became something else');
    }
    this.uploadId = null;
    this.set({ kind: 'done', item: done.item, existing: false });
  }

  private async sendPart(part: { n: number; url: string; size: number }): Promise<void> {
    const from = (part.n - 1) * this.partSize;
    for (let attempt = 1; ; attempt++) {
      if (this.stopping) {
        throw new Stopped();
      }
      this.controller = new AbortController();
      try {
        const res = await this.put(part.url, {
          method: 'PUT',
          body: this.file.slice(from, from + part.size),
          signal: this.controller.signal,
        });
        if (res.ok) {
          return;
        }
        if (attempt >= TRIES) {
          throw new ApiError(res.status, 'part_refused', `a part answered ${res.status}`);
        }
      } catch (error) {
        if (this.stopping) {
          throw new Stopped();
        }
        if (attempt >= TRIES) {
          throw error instanceof ApiError ? error : new ApiError(0, 'offline', "a part didn't arrive");
        }
      } finally {
        this.controller = null;
      }
      await this.wait(500 * attempt);
    }
  }

  private stopped(error: unknown): void {
    if (this.stopping === 'cancel') {
      return;
    }
    if (this.stopping === 'pause' || error instanceof Stopped) {
      this.set({ kind: 'paused', sent: this.sent, total: this.file.size });
      return;
    }
    this.set({ kind: 'failed', error, sent: this.sent, total: this.file.size });
  }
}
